import React, { useRef, useState, useEffect, memo } from 'react';
import { SelectChangeEvent, Typography } from "@mui/material";
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { LandingZone } from '@m4a3/api-client';

interface OpsHeaderProps {
  selectedLandingZone: string;
  landingZones: LandingZone[];
  logoSrc?: any;
  handleLZUpdate: (event: SelectChangeEvent<any>) => void;
  isConnected?: boolean;
}

const OpsHeader = ({
  selectedLandingZone,
  landingZones,
  logoSrc,
  handleLZUpdate,
  isConnected = false,
}: OpsHeaderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  
  // Find the current landing zone name to display
  const currentLandingZone = landingZones?.find(lz => lz.id === selectedLandingZone);
  const currentLandingZoneName = currentLandingZone?.name || 'Select Location';

  // Handle dropdown toggle
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Handle selection of a landing zone
  const handleSelection = (lzId: string) => {
    const compatibleEvent = {
      target: {
        value: lzId,
        name: 'landingZone'
      }
    } as SelectChangeEvent<any>;
    
    handleLZUpdate(compatibleEvent);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);
  
  return (
    <div className="border-b border-gray-200" style={{ paddingTop: '16px' }}>
      <div className="flex items-center w-full py-4">
        {/* Logo on the left */}
        {logoSrc && (
          <div style={{ marginLeft: '16px' }} className="flex items-center">
            <img 
              style={{ 
                width: '144px', 
                height: '56px',
              }} 
              alt="Manna Weigher App Icon" 
              src={logoSrc}
            />
            
            {isConnected ? (
              <div className="flex items-center ml-4">
                <span className="relative flex h-3 w-3 mr-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
                <Typography variant="body1" className="text-green-700 font-medium">
                  Live Updates
                </Typography>
              </div>
            ) : (
              <div className="flex items-center ml-4">
                <span className="relative flex h-3 w-3 mr-2">
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                </span>
                <Typography variant="body1" className="text-red-700 font-medium">
                  Disconnected
                </Typography>
              </div>
            )}
          </div>
        )}
        {/* Push dropdown to the right side */}
        <div className="flex-grow"></div>
        <div ref={wrapperRef} className="relative min-w-[200px]" style={{ marginRight: '100px' }}>
          <button 
            onClick={toggleDropdown}
            className="flex items-center justify-center cursor-pointer"
          >
            <span className="text-2xl font-bold">{currentLandingZoneName}</span>
            <ChevronDownIcon className="h-5 w-5 ml-2" />
          </button>
          {isOpen && (
            <div className="absolute mt-2 bg-white rounded-md shadow-lg z-50 right-0"
                 style={{ 
                   minWidth: '250px', 
                   width: 'auto'
                 }}>
              <ul className="py-1">
                {(landingZones || []).map((lz: LandingZone) => (
                  <li 
                    key={lz.id}
                    onClick={() => handleSelection(lz.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleSelection(lz.id);
                      }
                    }}
                    tabIndex={0}
                    className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-lg whitespace-nowrap ${
                      lz.id === selectedLandingZone ? 'bg-gray-100 font-semibold' : ''
                    }`}
                  >
                    {lz.name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(OpsHeader);